import { useQuery } from "@tanstack/react-query";
import { api, endpoints } from "../lib/api";
import Topbar from "../components/Topbar";
import { toast } from "sonner";
import { Copy, MessageCircle, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";

const GOAL_LABEL = {
  recuperar_cliente: "Reactivar clienta",
  vender_membresia: "Vender membresía",
  seguimiento_vip: "Seguimiento VIP",
  confirmar_cita: "Confirmar cita",
  recordar_renovacion: "Recordar renovación",
};

const CHANNEL = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  email: { label: "Email", icon: Mail, color: "#1A1A1A" },
  llamada: { label: "Llamada", icon: Phone, color: "#4A4A4A" },
};

export default function Marketing() {
  const { data: suggestions = [] } = useQuery({ queryKey: ["marketing"], queryFn: () => api.get(endpoints.marketing).then((r) => r.data) });

  const grouped = suggestions.reduce((acc, s) => {
    (acc[s.goal] ||= []).push(s);
    return acc;
  }, {});

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mensaje copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div>
      <Topbar
        title="Marketing"
        subtitle="Campañas sugeridas, segmentación y mensajes recomendados. Copia el texto y envíalo desde tu canal."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="marketing-segments">
        <SegmentCard eyebrow="Segmento" title="Clientas para reactivación" hint="Sin visita en 60+ días · candidatas a campaña" count={(grouped.recuperar_cliente || []).length} />
        <SegmentCard eyebrow="Segmento" title="Nuevas · potencial membresía" hint="Onboarding y conversión a plan mensual" count={(grouped.vender_membresia || []).length} />
        <SegmentCard eyebrow="Segmento" title="VIP · seguimiento premium" hint="Mantén la experiencia excepcional" count={(grouped.seguimiento_vip || []).length} />
      </div>

      <div className="space-y-3" data-testid="marketing-suggestions">
        {suggestions.map((s) => {
          const ch = CHANNEL[s.channel];
          const Icon = ch.icon;
          const key = `${s.client_id}-${s.goal}`;
          return (
            <div key={key} className="laka-card p-6 flex flex-col md:flex-row gap-4">
              <div className="md:w-1/4">
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--laka-text)" }}>Cliente</div>
                <div className="font-medium text-sm mt-1">{s.client_name}</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: "#F5EFE7" }}>
                    <Icon size={12} strokeWidth={1.5} color={ch.color} /> {ch.label}
                  </span>
                </div>
                <div className="mt-2 text-[11px] px-2 py-0.5 rounded-full inline-block" style={{ backgroundColor: "#E8DFD1", color: "#1A1A1A" }}>
                  {GOAL_LABEL[s.goal] || s.goal}
                </div>
              </div>
              <div className="flex-1 border-l laka-hairline pl-4">
                <p className="text-sm leading-relaxed">{s.message}</p>
                <Button
                  data-testid={`marketing-copy-${s.client_id}-${s.goal}`}
                  size="sm"
                  onClick={() => copy(s.message)}
                  className="mt-3 laka-btn-primary rounded-md"
                >
                  <Copy size={12} strokeWidth={1.5} className="mr-1" /> Copiar mensaje
                </Button>
              </div>
            </div>
          );
        })}
        {suggestions.length === 0 && (
          <div className="laka-card p-12 text-center text-sm" style={{ color: "var(--laka-text)" }}>
            No hay sugerencias de campaña en este momento.
          </div>
        )}
      </div>

      <div className="mt-8 laka-card p-6 bg-[#F5EFE7]">
        <div className="laka-eyebrow">Próximas integraciones</div>
        <div className="text-sm mt-1" style={{ color: "var(--laka-text)" }}>
          En próximas versiones LAKA Business Studio conectará con WhatsApp Business y Email para envíos automatizados.
        </div>
      </div>
    </div>
  );
}

function SegmentCard({ eyebrow, title, hint, count }) {
  return (
    <div className="laka-card p-6">
      <span className="laka-eyebrow">{eyebrow}</span>
      <div className="font-display text-2xl leading-tight mt-1">{title}</div>
      <div className="text-xs mt-2" style={{ color: "var(--laka-text)" }}>{hint}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-4xl" style={{ color: "#D6B36A" }}>{count}</span>
        <span className="text-xs" style={{ color: "var(--laka-text)" }}>clientas</span>
      </div>
    </div>
  );
}
